import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/AuthContext';
import { Form, Input, Button, Checkbox, Typography, ConfigProvider, theme, Modal } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { trimFormValues } from '../../utils/trimFormValues';

const { Title, Text } = Typography;

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [signUpModal, setSignUpModal] = useState(false);
  const { login, isAuthenticated, error, clearError } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) navigate('/');
    if (error) { toast.error(error); clearError(); }
  }, [isAuthenticated, error, navigate, clearError]);

  const onFinish = async (values) => {
    const trimmedValues = trimFormValues(values);
    setLoading(true);
    const result = await login(trimmedValues);
    setLoading(false);
    if (result.success) { toast.success('Login successful'); navigate('/'); }
    else toast.error(result.error || 'Login failed');
  };

  return (
    // Force light mode for the entire login page regardless of app theme
    <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm, token: { colorPrimary: '#1677ff', borderRadius: 10 } }}>
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: 'linear-gradient(135deg, #e8edf5 0%, #f0f4fb 50%, #e4eaf4 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative blobs — subtle on light bg */}
        <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
          <div style={{
            position: 'absolute', width: 600, height: 600, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(99,60,255,0.08) 0%, transparent 70%)',
            top: '-15%', left: '-10%',
          }} />
          <div style={{
            position: 'absolute', width: 500, height: 500, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(22,119,255,0.1) 0%, transparent 70%)',
            bottom: '-10%', right: '-10%',
          }} />
        </div>

        {/* Card */}
        <div style={{
          position: 'relative', zIndex: 1,
          width: '100%', maxWidth: 420,
          background: '#ffffff',
          borderRadius: 20,
          overflow: 'hidden',
          boxShadow: '0 4px 6px rgba(0,0,0,0.04), 0 20px 60px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.06)',
        }}>
          {/* Gradient accent bar */}
          <div style={{
            height: 4,
            background: 'linear-gradient(90deg, #6330f5 0%, #1677ff 50%, #00b4d8 100%)',
          }} />

          <div style={{ padding: '36px 36px 32px' }}>
            {/* Logo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
              <div style={{
                width: 38, height: 38, borderRadius: 10,
                background: 'linear-gradient(135deg, #1677ff 0%, #6330f5 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 14, color: '#fff', letterSpacing: '-0.3px',
                flexShrink: 0,
              }}>
                LT
              </div>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e' }}>
                LITE Transport
              </span>
            </div>

            {/* Heading */}
            <div style={{ marginBottom: 28 }}>
              <Title level={3} style={{ margin: '0 0 4px', fontWeight: 700, color: '#0f0f1a', letterSpacing: '-0.4px' }}>
                Welcome back
              </Title>
              <Text style={{ color: '#94a3b8', fontSize: 14 }}>
                Sign in to access your dashboard
              </Text>
            </div>

            {/* Form */}
            <Form name="login" onFinish={onFinish} layout="vertical">
              <Form.Item
                name="email"
                label={<span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Email address</span>}
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Enter a valid email' },
                ]}
                style={{ marginBottom: 16 }}
              >
                <Input
                  size="large"
                  prefix={<UserOutlined style={{ color: '#c0c8d8', fontSize: 14 }} />}
                  placeholder="you@example.com"
                  autoComplete="email"
                  style={{ borderColor: '#e2e8f0', borderRadius: 10, height: 46 }}
                />
              </Form.Item>

              <Form.Item
                name="password"
                label={<span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Password</span>}
                rules={[{ required: true, message: 'Please enter your password' }]}
                style={{ marginBottom: 14 }}
              >
                <Input.Password
                  size="large"
                  prefix={<LockOutlined style={{ color: '#c0c8d8', fontSize: 14 }} />}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  style={{ borderColor: '#e2e8f0', borderRadius: 10, height: 46 }}
                />
              </Form.Item>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Form.Item name="remember" valuePropName="checked" noStyle>
                  <Checkbox style={{ fontSize: 13, color: '#64748b' }}>Remember me</Checkbox>
                </Form.Item>
                <Link to="/forgot-password" style={{ fontSize: 13, fontWeight: 500, color: '#1677ff' }}>
                  Forgot password?
                </Link>
              </div>

              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
                style={{
                  height: 46, borderRadius: 10, fontWeight: 600, fontSize: 15,
                  background: 'linear-gradient(135deg, #1677ff 0%, #6330f5 100%)',
                  border: 'none',
                  boxShadow: '0 4px 14px rgba(22,119,255,0.3)',
                }}
              >
                Sign in
              </Button>
            </Form>

            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <Text style={{ color: '#94a3b8', fontSize: 13 }}>
                Don't have an account?{' '}
                <button onClick={() => setSignUpModal(true)} style={{ color: '#1677ff', fontWeight: 600, cursor: 'pointer', background: 'none', border: 'none', padding: 0, font: 'inherit' }}>Sign up</button>
              </Text>
            </div>
          </div>
        </div>
      </div>
      <Modal
        open={signUpModal}
        onCancel={() => setSignUpModal(false)}
        footer={<Button type="primary" onClick={() => setSignUpModal(false)}>Got it</Button>}
        centered
        title="Account Access"
      >
        <p style={{ margin: '16px 0', color: '#374151', lineHeight: 1.7 }}>
          Accounts are created by the administrator. Please contact your admin to get access.
        </p>
      </Modal>
    </ConfigProvider>
  );
};

export default Login;
